// @flow

import OptionManager from "./OptionManager";
import type {PhaseState} from "../components/CalculationPhaseInput";

import type {
    CalculationInputInsertionFlexibleLining,
    CalculationInputTube,
    CalculationInputTubeCracking,
    CalculationRequestProjectInput,
    CalculationInputDirectedDrilling,
    CalculationInputSliplining, CalculationInputPuLiner, CalculationInputTubeWithoutMaterialAndType
} from './RequestTypes';
import type {CalculationInputTubeWithoutMaterial} from './RequestTypes';
import type {CalculationRequestInput} from './RequestTypes';
import type {ProjectUiInput} from "../store/Calculation";
import DefaultValueManager from "./DefaultValueManager";

export default class RequestDataCreatorInverse {

    optionManager: OptionManager;
    defaultValueManager: DefaultValueManager;

    constructor(optionManager: OptionManager, defaultValueManager: DefaultValueManager) {
        this.optionManager = optionManager;
        this.defaultValueManager = defaultValueManager;
    }

    createProjectState(projectInput: CalculationRequestProjectInput): ProjectUiInput {
        return {
            touched: {},
            error: {},
            projectName: projectInput.projectName,
            organisationName: projectInput.organization,
            personName: projectInput.personName,
            version: projectInput.frontEndVersion,
            phaseStates: projectInput.phases.map(phase => this.createPhaseState(phase))
        };
    }
    
    createPhaseState(phaseInput: CalculationRequestInput) : PhaseState {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let existingTubes = this.createTubeState('existingTube', 3, phaseInput.basicInfoBefore.tubes); 
        let traditionalTubes = this.createTubeState('traditionalTube', 3, phaseInput.traditionalDigging.tubes); 
        let retVal = {
            touched: {},
            error: {},
            techniquesToCompare: opMgr.getIdFromServerStrValue(phaseInput.techniquesToCompare, sel.techniquesToCompare),
            typeOfLandscapeBefore: opMgr.getIdFromServerStrValue(phaseInput.basicInfoBefore.typeOfLandscape, sel.typeOfLandscape),
            typeOfLandscapeAfter: opMgr.getIdFromServerStrValue(phaseInput.basicInfoAfter.typeOfLandscape, sel.typeOfLandscape),
            coatingBefore: opMgr.getIdFromServerStrValue(phaseInput.basicInfoBefore.coating, sel.coating),
            coatingAfter: opMgr.getIdFromServerStrValue(phaseInput.basicInfoAfter.coating, sel.coating),
            streetTypeBefore: opMgr.getIdFromServerStrValue(phaseInput.basicInfoBefore.streetType, sel.streetType),
            streetTypeAfter: opMgr.getIdFromServerStrValue(phaseInput.basicInfoAfter.streetType, sel.streetType),
            asphaltTypeAfter: opMgr.getIdFromServerStrValue(phaseInput.basicInfoAfter.asphaltType, sel.asphaltType),
            
            numExistingTubes: phaseInput.basicInfoBefore.tubes.length,
            ...existingTubes,

            distanceReceiverFillingKm: phaseInput.basicInfoBefore.distanceReceiverFillingKm,
            distanceReceiverCleanAsphaltKm: phaseInput.basicInfoBefore.distanceReceiverCleanAsphaltKm,
            distanceReceiverPollutedAsphaltKm: phaseInput.basicInfoBefore.distanceReceiverPollutedAsphaltKm,
            distanceFillingKm: phaseInput.basicInfoAfter.distanceFillingKm,
            distanceReceiverOldTubesKm: phaseInput.traditionalDigging.distanceReceiverOldTubesKm,

            shaftDepthM: phaseInput.traditionalDigging.shaftDepthM,
            slopeAngleN1: opMgr.getIdFromServerNumValue(phaseInput.traditionalDigging.slopeAngleN1, sel.slopeAngle),
            shaftCapacityM3PerHour: phaseInput.traditionalDigging.shaftCapacityM3PerHour,
            shaftLengthM: phaseInput.traditionalDigging.shaftLengthM,

            numTraditionalTubes: phaseInput.traditionalDigging.tubes.length,
            ...traditionalTubes,
            
            ...this.createIflState(phaseInput.insertionFlexibleLining),
            ...this.createDdState(phaseInput.directedDrilling),
            ...this.createTcState(phaseInput.tubeCracking),
            ...this.createSlState(phaseInput.sliplining),
            ...this.createPulState(phaseInput.puLiner),
            
        };
        return retVal;
    }
    
    createIflState(input: ?CalculationInputInsertionFlexibleLining): {[name:string]: number} {
        if(input) {
            return {
                iflPointShafts: input.pointShafts,
                iflMetersLongerBottomLengthThanWidth: input.pointShafts > 0 ? input.metersLongerBottomLengthThanWidth
                    : this.defaultValueManager.getDefaultValue('iflMetersLongerBottomLengthThanWidth'),
                iflShaftDepthM: input.pointShafts > 0 ? input.shaftDepthM
                    : this.defaultValueManager.getDefaultValue('iflShaftDepthM'),
                iflSlopeAngleN1: input.pointShafts > 0 ? input.slopeAngleN1
                    : this.defaultValueManager.getDefaultValue('iflSlopeAngleN1'),
                iflShaftCapacityM3PerHour: input.pointShafts > 0 ? input.shaftCapacityM3PerHour :
                    this.defaultValueManager.getDefaultValue('iflShaftCapacityM3PerHour'),
                iflShaftLengthM: input.pointShafts > 0 ? input.phaseLengthM
                    : this.defaultValueManager.getDefaultValue('iflShaftLengthM'),

                numIflTubes: input.tubes.length,
                ...this.createTubeWithoutMaterialState('iflTube', 2, input.tubes)
            }
        }
        else
            return {
                iflPointShafts: this.defaultValueManager.getDefaultValue('iflPointShafts'),
                iflMetersLongerBottomLengthThanWidth: this.defaultValueManager.getDefaultValue('iflMetersLongerBottomLengthThanWidth'),
                iflShaftDepthM: this.defaultValueManager.getDefaultValue('iflShaftDepthM'),
                iflSlopeAngleN1: this.defaultValueManager.getDefaultValue('iflSlopeAngleN1'),
                iflShaftCapacityM3PerHour: this.defaultValueManager.getDefaultValue('iflShaftCapacityM3PerHour'),
                iflShaftLengthM: this.defaultValueManager.getDefaultValue('iflShaftLengthM'),

                numIflTubes: this.defaultValueManager.getDefaultValue('numIflTubes'),
                ...this.createTubeWithoutMaterialState('iflTube', 2, null)
            };
    }
    
    createTcState(input: ?CalculationInputTubeCracking): {[name:string]: number} {
        if(input) {
            return {
                tcPointShafts: input.pointShafts,
                tcMetersLongerBottomLengthThanWidth: input.pointShafts > 0 ? input.metersLongerBottomLengthThanWidth
                    : this.defaultValueManager.getDefaultValue('tcMetersLongerBottomLengthThanWidth'),
                tcShaftDepthM: input.pointShafts > 0 ? input.shaftDepthM
                    : this.defaultValueManager.getDefaultValue('tcShaftDepthM'),
                tcSlopeAngleN1: input.pointShafts > 0 ? input.slopeAngleN1
                    : this.defaultValueManager.getDefaultValue('tcSlopeAngleN1'),
                tcShaftCapacityM3PerHour: input.pointShafts > 0 ? input.shaftCapacityM3PerHour :
                    this.defaultValueManager.getDefaultValue('tcShaftCapacityM3PerHour'),
                tcShaftLengthM: input.pointShafts > 0 ? input.phaseLengthM
                    : this.defaultValueManager.getDefaultValue('tcShaftLengthM'),

                numTcTubes: input.tubes.length,
                ...this.createTubeState('tcTube', 3, input.tubes)
            }
        }
        else
            return {
                tcPointShafts: this.defaultValueManager.getDefaultValue('tcPointShafts'),
                tcMetersLongerBottomLengthThanWidth: this.defaultValueManager.getDefaultValue('tcMetersLongerBottomLengthThanWidth'),
                tcShaftDepthM: this.defaultValueManager.getDefaultValue('tcShaftDepthM'),
                tcSlopeAngleN1: this.defaultValueManager.getDefaultValue('tcSlopeAngleN1'),
                tcShaftCapacityM3PerHour: this.defaultValueManager.getDefaultValue('tcShaftCapacityM3PerHour'),
                tcShaftLengthM: this.defaultValueManager.getDefaultValue('tcShaftLengthM'),

                numTcTubes: this.defaultValueManager.getDefaultValue('numTcTubes'),
                ...this.createTubeState('tcTube', 3, null)
            };
    }
    
    createDdState(input: ?CalculationInputDirectedDrilling): {[name:string]: number} {
        if(input) {
            return {
                ddPointShafts: input.pointShafts,
                ddMetersLongerBottomLengthThanWidth: input.pointShafts > 0 ? input.metersLongerBottomLengthThanWidth
                    : this.defaultValueManager.getDefaultValue('ddMetersLongerBottomLengthThanWidth'),
                ddShaftDepthM: input.pointShafts > 0 ? input.shaftDepthM
                    : this.defaultValueManager.getDefaultValue('ddShaftDepthM'),
                ddSlopeAngleN1: input.pointShafts > 0 ? input.slopeAngleN1
                    : this.defaultValueManager.getDefaultValue('ddSlopeAngleN1'),
                ddShaftCapacityM3PerHour: input.pointShafts > 0 ? input.shaftCapacityM3PerHour :
                    this.defaultValueManager.getDefaultValue('ddShaftCapacityM3PerHour'),
                ddShaftLengthM: input.pointShafts > 0 ? input.phaseLengthM
                    : this.defaultValueManager.getDefaultValue('ddShaftLengthM'),

                numDdTubes: input.tubes.length,
                ...this.createTubeState('ddTube', 3, input.tubes)
            }
        }
        else
            return {
                ddPointShafts: this.defaultValueManager.getDefaultValue('ddPointShafts'),
                ddMetersLongerBottomLengthThanWidth: this.defaultValueManager.getDefaultValue('ddMetersLongerBottomLengthThanWidth'),
                ddShaftDepthM: this.defaultValueManager.getDefaultValue('ddShaftDepthM'),
                ddSlopeAngleN1: this.defaultValueManager.getDefaultValue('ddSlopeAngleN1'),
                ddShaftCapacityM3PerHour: this.defaultValueManager.getDefaultValue('ddShaftCapacityM3PerHour'),
                ddShaftLengthM: this.defaultValueManager.getDefaultValue('ddShaftLengthM'),

                numDdTubes: this.defaultValueManager.getDefaultValue('numDdTubes'),
                ...this.createTubeState('ddTube', 3, null)
            };
    }

    createSlState(input: ?CalculationInputSliplining): {[name:string]: number} {
        if(input) {
            return {
                slPointShafts: input.pointShafts,
                slMetersLongerBottomLengthThanWidth: input.pointShafts > 0 ? input.metersLongerBottomLengthThanWidth
                    : this.defaultValueManager.getDefaultValue('slMetersLongerBottomLengthThanWidth'),
                slShaftDepthM: input.pointShafts > 0 ? input.shaftDepthM
                    : this.defaultValueManager.getDefaultValue('slShaftDepthM'),
                slSlopeAngleN1: input.pointShafts > 0 ? input.slopeAngleN1
                    : this.defaultValueManager.getDefaultValue('slSlopeAngleN1'),
                slShaftCapacityM3PerHour: input.pointShafts > 0 ? input.shaftCapacityM3PerHour :
                    this.defaultValueManager.getDefaultValue('slShaftCapacityM3PerHour'),
                slShaftLengthM: input.pointShafts > 0 ? input.phaseLengthM
                    : this.defaultValueManager.getDefaultValue('slShaftLengthM'),

                numSlTubes: input.tubes.length,
                ...this.createTubeState('slTube', 1, input.tubes)
            }
        }
        else
            return {
                slPointShafts: this.defaultValueManager.getDefaultValue('slPointShafts'),
                slMetersLongerBottomLengthThanWidth: this.defaultValueManager.getDefaultValue('slMetersLongerBottomLengthThanWidth'),
                slShaftDepthM: this.defaultValueManager.getDefaultValue('slShaftDepthM'),
                slSlopeAngleN1: this.defaultValueManager.getDefaultValue('slSlopeAngleN1'),
                slShaftCapacityM3PerHour: this.defaultValueManager.getDefaultValue('slShaftCapacityM3PerHour'),
                slShaftLengthM: this.defaultValueManager.getDefaultValue('slShaftLengthM'),

                numIflTubes: this.defaultValueManager.getDefaultValue('numIflTubes'),
                ...this.createTubeWithoutMaterialState('slTube', 1, null)
            };
    }

    createPulState(input: ?CalculationInputPuLiner): {[name:string]: number} {
        if(input) {
            return {
                pulPointShafts: input.pointShafts,
                pulMetersLongerBottomLengthThanWidth: input.pointShafts > 0 ? input.metersLongerBottomLengthThanWidth
                    : this.defaultValueManager.getDefaultValue('pulMetersLongerBottomLengthThanWidth'),
                pulShaftDepthM: input.pointShafts > 0 ? input.shaftDepthM
                    : this.defaultValueManager.getDefaultValue('pulShaftDepthM'),
                pulSlopeAngleN1: input.pointShafts > 0 ? input.slopeAngleN1
                    : this.defaultValueManager.getDefaultValue('pulSlopeAngleN1'),
                pulShaftCapacityM3PerHour: input.pointShafts > 0 ? input.shaftCapacityM3PerHour :
                    this.defaultValueManager.getDefaultValue('pulShaftCapacityM3PerHour'),
                pulShaftLengthM: input.pointShafts > 0 ? input.phaseLengthM
                    : this.defaultValueManager.getDefaultValue('pulShaftLengthM'),

                numPulTubes: input.tubes.length,
                ...this.createTubeWithoutMaterialState('pulTube', 1, input.tubes)
            }
        }
        else
            return {
                pulPointShafts: this.defaultValueManager.getDefaultValue('pulPointShafts'),
                pulMetersLongerBottomLengthThanWidth: this.defaultValueManager.getDefaultValue('pulMetersLongerBottomLengthThanWidth'),
                pulShaftDepthM: this.defaultValueManager.getDefaultValue('pulShaftDepthM'),
                pulSlopeAngleN1: this.defaultValueManager.getDefaultValue('pulSlopeAngleN1'),
                pulShaftCapacityM3PerHour: this.defaultValueManager.getDefaultValue('pulShaftCapacityM3PerHour'),
                pulShaftLengthM: this.defaultValueManager.getDefaultValue('pulShaftLengthM'),

                numPulTubes: this.defaultValueManager.getDefaultValue('numPulTubes'),
                ...this.createTubeWithoutMaterialAndTypeState('pulTube', 1, null)
            };
    }


    createTubeState(prefix: string, maxTubes: number, tubes: ?CalculationInputTube[]): {[name:string]: number} {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let retVal = {};

        tubes = this.setDefaultMissingTubeTypesAndMaterials(prefix, tubes);
        
        for(let i = 1; i <= maxTubes; i++) {
            let tubeIdx = i - 1;
            retVal[`${prefix}${i}TubeType`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerStrValue(tubes[tubeIdx].tubeType, sel.tubeType) 
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeType`);
            retVal[`${prefix}${i}TubeMaterial`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerStrValue(tubes[tubeIdx].tubeMaterial, sel.tubeMaterialAll)
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeMaterial`);
            retVal[`${prefix}${i}TubeDiameter`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerNumValue(tubes[tubeIdx].tubeDiameterMm, sel.tubeDiameterAll)
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeDiameter`);
            retVal[`${prefix}${i}TubeLength`] = tubes && (i <= tubes.length) ? tubes[tubeIdx].tubeLengthM
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeLength`);
        }
        return retVal;
    }
    
    createTubeWithoutMaterialState(prefix: string, maxTubes: number, tubes: ?CalculationInputTubeWithoutMaterial[]): {[name:string]: number} {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let retVal = {};

        tubes = this.setDefaultMissingTubeTypes(prefix, tubes);
        
        for(let i = 1; i <= maxTubes; i++) {
            let tubeIdx = i - 1;
            retVal[`${prefix}${i}TubeType`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerStrValue(tubes[tubeIdx].tubeType, sel.tubeType) 
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeType`);
            retVal[`${prefix}${i}TubeDiameter`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerNumValue(tubes[tubeIdx].tubeDiameterMm, sel.tubeDiameterAll)
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeDiameter`);
            retVal[`${prefix}${i}TubeLength`] = tubes && (i <= tubes.length) ? tubes[tubeIdx].tubeLengthM
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeLength`);
        }
        
        
        return retVal;
    }
    
    createTubeWithoutMaterialAndTypeState(prefix: string, maxTubes: number, tubes: ?CalculationInputTubeWithoutMaterialAndType[]): {[name:string]: number} {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        let retVal = {};

        for(let i = 1; i <= maxTubes; i++) {
            let tubeIdx = i - 1;
            retVal[`${prefix}${i}TubeDiameter`] = tubes && (i <= tubes.length) ? opMgr.getIdFromServerNumValue(tubes[tubeIdx].tubeDiameterMm, sel.tubeDiameterAll)
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeDiameter`);
            retVal[`${prefix}${i}TubeLength`] = tubes && (i <= tubes.length) ? tubes[tubeIdx].tubeLengthM
                : this.defaultValueManager.getDefaultValue(`${prefix}${i}TubeLength`);
        }
        return retVal;
    }
    
    setDefaultMissingTubeTypes(prefix: string, tubes: ?CalculationInputTubeWithoutMaterial[]): ?CalculationInputTubeWithoutMaterial[] {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        if(tubes != null) {
            tubes.forEach(t => {

                // Special handling of tube type for PU lining. No type is stored on server, always water and stored in UI state.
                if(prefix === 'pulTube' && t.tubeType == null)
                    t.tubeType = opMgr.getServerStrValue(this.optionManager.ids.TUBE_TYPE_WATER, sel.tubeType);

                // Special handling of tube type for Sliplining. From beginning there was to tube type choice but later added in UI. However to make it work with storage of old calculations later added to backend too, so may or may not be set in stored calculation.
                if(prefix === 'slTube' && t.tubeType == null)
                    t.tubeType = opMgr.getServerStrValue(this.optionManager.ids.TUBE_TYPE_WATER, sel.tubeType);
                
            });
            
            return tubes;
        }
        else
            return tubes;
    }

    setDefaultMissingTubeTypesAndMaterials(prefix: string, tubes: ?(CalculationInputTube)[]): ?CalculationInputTube[] {
        let opMgr = this.optionManager;
        let sel = opMgr.availableSelections;

        if(tubes != null) {
            tubes.forEach(t => {

                // Special handling of tube type for PU lining. No type is stored on server, always water and stored in UI state.
                if(prefix === 'pulTube' && t.tubeType == null)
                    t.tubeType = opMgr.getServerStrValue(this.optionManager.ids.TUBE_TYPE_WATER, sel.tubeType);

                // Special handling of tube type for Sliplining. From beginning there was to tube type choice but later added in UI. However to make it work with storage of old calculations later added to backend too, so may or may not be set in stored calculation.
                if(prefix === 'slTube' && t.tubeType == null)
                    t.tubeType = opMgr.getServerStrValue(this.optionManager.ids.TUBE_TYPE_WATER, sel.tubeType);

                // Special handling of tube material for Sliplining. From beginning there was no tube material choice but later added in UI. However to make it work with storage of old calculations later added to backend too, so may or may not be set in stored calculation.
                if(prefix === 'slTube' && t.tubeMaterial == null)
                    t.tubeMaterial = opMgr.getServerStrValue(this.optionManager.ids.TUBE_MATERIAL_PE, sel.tubeMaterialAll);
                
            });
            
            return tubes;
        }
        else
            return tubes;
    }
    
}


// WEBPACK FOOTER //
// ./src/common/RequestDataCreatorInverse.js